/**
 * AI Assistant API
 * API functions for interacting with the Claude-powered AI assistant.
 */

import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import type {
  AIChatRequest,
  AIChatResponse,
  AIConversation,
  AIConversationsListResponse,
  AIMessagesListResponse,
  AICapabilitiesResponse,
} from '@/types/ai-assistant';

const AI_API_BASE = ROUTES.API.AI_ASSISTANT;

/**
 * Send a message to the AI assistant
 */
export async function sendChatMessage(request: AIChatRequest): Promise<AIChatResponse> {
  // AI turns with several tool round-trips can take minutes — don't use the default timeout
  const response = await api.post<AIChatResponse>(`${AI_API_BASE}chat/`, request, {
    timeout: 300_000,
  });
  return response.data;
}

export interface AIStreamHandlers {
  /** Called with each chunk of assistant text as it is generated. */
  onDelta?: (text: string) => void;
  /** Called when the assistant starts using a tool (detail = compact input summary). */
  onTool?: (tool: string, detail?: string) => void;
  /** Called on keep-alive pings (the assistant is thinking, no visible output). */
  onPing?: () => void;
  /** Called when a stronger model was picked for this task. */
  onModel?: (model: string) => void;
}

/**
 * Send a message via the streaming (SSE) endpoint. Resolves with the final
 * response once the stream completes. Throws { code: 'NO_STREAM' } when the
 * backend doesn't support streaming yet, so callers can fall back.
 */
export async function streamChatMessage(
  request: AIChatRequest,
  handlers: AIStreamHandlers = {},
): Promise<AIChatResponse> {
  const base = api.defaults.baseURL || '';
  const response = await fetch(`${base}${AI_API_BASE}chat/stream/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
    },
    body: JSON.stringify(request),
  });

  if (response.status === 404 || response.status === 405) {
    throw Object.assign(new Error('Streaming not available'), { code: 'NO_STREAM' });
  }
  if (!response.ok || !response.body) {
    let detail = 'Failed to get a response from the AI assistant';
    try {
      const data = await response.json();
      detail = data.detail || data.error || detail;
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new Error(detail);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let final: AIChatResponse | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    for (const raw of events) {
      const line = raw.trim();
      if (!line.startsWith('data: ')) continue;
      const event = JSON.parse(line.slice(6));
      if (event.type === 'delta') {
        handlers.onDelta?.(event.text);
      } else if (event.type === 'tool_start') {
        handlers.onTool?.(event.tool, event.detail);
      } else if (event.type === 'ping') {
        handlers.onPing?.();
      } else if (event.type === 'model') {
        handlers.onModel?.(event.model);
      } else if (event.type === 'error') {
        throw new Error(event.error);
      } else if (event.type === 'done') {
        final = {
          conversation_id: event.conversation_id,
          message: event.message,
          tool_calls: event.tool_calls,
          tokens_used: event.tokens_used,
        };
      }
    }
  }

  if (!final) {
    throw new Error('The connection was interrupted before the response finished.');
  }
  return final;
}

/**
 * Upload a file for use as a chat attachment. Text is extracted server-side.
 */
export async function uploadAIAttachment(file: File): Promise<import('@/types/ai-assistant').AIAttachment> {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post(`${AI_API_BASE}upload/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  });
  return response.data;
}

/**
 * Get all conversations for the current user
 */
export async function getConversations(): Promise<AIConversationsListResponse> {
  const response = await api.get<AIConversationsListResponse>(`${AI_API_BASE}conversations/`);
  return response.data;
}

/**
 * Get a specific conversation with all messages
 */
export async function getConversation(id: number): Promise<AIConversation> {
  const response = await api.get<AIConversation>(`${AI_API_BASE}conversations/${id}/`);
  return response.data;
}

/**
 * Get messages for a specific conversation
 */
export async function getConversationMessages(id: number): Promise<AIMessagesListResponse> {
  const response = await api.get<AIMessagesListResponse>(`${AI_API_BASE}conversations/${id}/messages/`);
  return response.data;
}

/**
 * Archive a conversation
 */
export async function archiveConversation(id: number): Promise<void> {
  await api.delete(`${AI_API_BASE}conversations/${id}/`);
}

/**
 * Update conversation title
 */
export async function updateConversationTitle(id: number, title: string): Promise<AIConversation> {
  const response = await api.patch<AIConversation>(`${AI_API_BASE}conversations/${id}/`, { title });
  return response.data;
}

/**
 * Clear all messages in a conversation
 */
export async function clearConversation(id: number): Promise<void> {
  await api.post(`${AI_API_BASE}conversations/${id}/clear/`);
}

/**
 * Get AI capabilities information
 */
export async function getCapabilities(): Promise<AICapabilitiesResponse> {
  const response = await api.get<AICapabilitiesResponse>(`${AI_API_BASE}capabilities/`);
  return response.data;
}
